import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { PageHeader, SectionContent, StyledList } from '../../components';
import { RECRUITMENT_POLICY } from '../../data';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const RecruitmentPolicyPage = ({ setRefs }) => {
  const recruitmentPolicyRef = useRef(null);
  const roleAndNoticeRef = useRef(null);
  const dataPrinciplesRef = useRef(null);
  const dataPrincipleDescriptionRef = useRef(null);
  const personalInfoCollectRef = useRef(null);
  const screeningIntroRef = useRef(null);
  const infoSourceRef = useRef(null);
  const infoUseRef = useRef(null);
  const infoUseClosingRef = useRef(null);
  const sensitiveInfoRef = useRef(null);
  const infoSharingRef = useRef(null);
  const infoSharingClosingRef = useRef(null);
  const internationalTransfersRef = useRef(null);
  const dataRetentionRef = useRef(null);
  const userRightsRef = useRef(null);
  const userRightsClosingRef = useRef(null);
  const contactUsRef = useRef(null);
  const complaintsRef = useRef(null);

  const refs = useMemo(
    () => ({
      recruitmentPolicy: recruitmentPolicyRef,
      roleAndNotice: roleAndNoticeRef,
      dataPrinciples: dataPrinciplesRef,
      dataPrincipleDescription: dataPrincipleDescriptionRef,
      personalInfoCollect: personalInfoCollectRef,
      screeningIntro: screeningIntroRef,
      infoSource: infoSourceRef,
      infoUse: infoUseRef,
      infoUseClosing: infoUseClosingRef,
      sensitiveInfo: sensitiveInfoRef,
      infoSharing: infoSharingRef,
      infoSharingClosing: infoSharingClosingRef,
      internationalTransfers: internationalTransfersRef,
      dataRetention: dataRetentionRef,
      userRights: userRightsRef,
      userRightsClosing: userRightsClosingRef,
      contactUs: contactUsRef,
      complaints: complaintsRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.recruitmentPolicyNotice.title}
        content={seoData.recruitmentPolicyNotice.content}
        keywords={seoData.recruitmentPolicyNotice.keywords}
        canonical={seoData.recruitmentPolicyNotice.canonical}
        ogImage={seoData.recruitmentPolicyNotice.ogImage}
      />
      {/* Page Header */}
      <PageHeader
        id='hero'
        title={RECRUITMENT_POLICY.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />

      {/* Introduction */}
      <SectionContent
        id='recruitmentPolicy'
        ref={refs.recruitmentPolicy}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.LAST_UPDATED,
            marginBottom: ELEMENT_SPACING,
            fontStyle: 'italic',
            fontSize: '1.1rem',
            color: THEME.COLORS.NEUTRAL_GRAY_DARK
          },
          ...RECRUITMENT_POLICY.INTRO.map(paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }))
        ]}
      />

      {/* Our Role and When This Privacy Notice Applies section */}
      <SectionContent
        id='roleAndNotice'
        ref={refs.roleAndNotice}
        title={RECRUITMENT_POLICY.SECTIONS.ROLE_AND_NOTICE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={RECRUITMENT_POLICY.SECTIONS.ROLE_AND_NOTICE.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Data Protection Principles section */}
      <SectionContent
        id='dataPrinciples'
        ref={refs.dataPrinciples}
        title={RECRUITMENT_POLICY.SECTIONS.DATA_PRINCIPLES.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.DATA_PRINCIPLES.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          variant='blue'
          items={RECRUITMENT_POLICY.SECTIONS.DATA_PRINCIPLES.LIST_ITEMS}
        />
      </Box>

      <SectionContent
        id='dataPrincipleDescription'
        ref={refs.dataPrincipleDescription}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.DATA_PRINCIPLES.CLOSING,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* What Personal Information Do We Collect section */}
      <SectionContent
        id='personalInfoCollect'
        ref={refs.personalInfoCollect}
        title={RECRUITMENT_POLICY.SECTIONS.PERSONAL_INFO_COLLECT.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.PERSONAL_INFO_COLLECT.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: SECTION_SPACING }}>
        <StyledList
          variant='blue'
          items={
            RECRUITMENT_POLICY.SECTIONS.PERSONAL_INFO_COLLECT.RECRUITMENT_LIST
          }
        />
      </Box>

      <SectionContent
        id='screeningIntro'
        ref={refs.screeningIntro}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.PERSONAL_INFO_COLLECT
              .SCREENING_INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          variant='blue'
          items={
            RECRUITMENT_POLICY.SECTIONS.PERSONAL_INFO_COLLECT.SCREENING_LIST
          }
        />
      </Box>

      {/* Where Do We Collect Your Personal Information From section */}
      <SectionContent
        id='infoSource'
        ref={refs.infoSource}
        title={RECRUITMENT_POLICY.SECTIONS.INFO_SOURCE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={RECRUITMENT_POLICY.SECTIONS.INFO_SOURCE.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* How Will We Use Your Personal Information section */}
      <SectionContent
        id='infoUse'
        ref={refs.infoUse}
        title={RECRUITMENT_POLICY.SECTIONS.INFO_USE.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.INFO_USE.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList items={RECRUITMENT_POLICY.SECTIONS.INFO_USE.LIST_ITEMS} />
      </Box>

      <SectionContent
        id='infoUseClosing'
        ref={refs.infoUseClosing}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.INFO_USE.CLOSING,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Sensitive Personal Information section */}
      <SectionContent
        id='sensitiveInfo'
        ref={refs.sensitiveInfo}
        title={RECRUITMENT_POLICY.SECTIONS.SENSITIVE_INFO.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={RECRUITMENT_POLICY.SECTIONS.SENSITIVE_INFO.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Who Do We Share Your Personal Information With section */}
      <SectionContent
        id='infoSharing'
        ref={refs.infoSharing}
        title={RECRUITMENT_POLICY.SECTIONS.INFO_SHARING.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.INFO_SHARING.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={RECRUITMENT_POLICY.SECTIONS.INFO_SHARING.LIST_ITEMS}
        />
      </Box>

      <SectionContent
        id='infoSharingClosing'
        ref={refs.infoSharingClosing}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.INFO_SHARING.CLOSING,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* International Transfers section */}
      <SectionContent
        id='internationalTransfers'
        ref={refs.internationalTransfers}
        title={RECRUITMENT_POLICY.SECTIONS.INTERNATIONAL_TRANSFERS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={RECRUITMENT_POLICY.SECTIONS.INTERNATIONAL_TRANSFERS.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* How Long Do We Keep Your Personal Information section */}
      <SectionContent
        id='dataRetention'
        ref={refs.dataRetention}
        title={RECRUITMENT_POLICY.SECTIONS.DATA_RETENTION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={RECRUITMENT_POLICY.SECTIONS.DATA_RETENTION.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />

      {/* Your Rights section */}
      <SectionContent
        id='userRights'
        ref={refs.userRights}
        title={RECRUITMENT_POLICY.SECTIONS.USER_RIGHTS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.USER_RIGHTS.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList
          items={RECRUITMENT_POLICY.SECTIONS.USER_RIGHTS.LIST_ITEMS}
        />
      </Box>

      <SectionContent
        id='userRightsClosing'
        ref={refs.userRightsClosing}
        initialSpacing={false}
        linkMappings={RECRUITMENT_POLICY.SECTIONS.USER_RIGHTS.LINKS.map(
          link => ({
            word: link.TEXT,
            url: link.URL,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          })
        )}
        text={[
          {
            text: RECRUITMENT_POLICY.SECTIONS.USER_RIGHTS.CONTACT,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Contacting Us section */}
      <SectionContent
        id='contactUs'
        ref={refs.contactUs}
        title={RECRUITMENT_POLICY.SECTIONS.CONTACT_US.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={RECRUITMENT_POLICY.SECTIONS.CONTACT_US.LINKS.map(
          link => ({
            word: link.TEXT,
            url: link.URL,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          })
        )}
        text={RECRUITMENT_POLICY.SECTIONS.CONTACT_US.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem',
            whiteSpace: paragraph.includes('\n') ? 'pre-line' : 'normal'
          })
        )}
      />

      {/* Complaints section */}
      <SectionContent
        id='complaints'
        ref={refs.complaints}
        title={RECRUITMENT_POLICY.SECTIONS.COMPLAINTS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={RECRUITMENT_POLICY.SECTIONS.COMPLAINTS.LINKS.map(
          link => ({
            word: link.TEXT,
            url: link.URL,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: true
          })
        )}
        text={RECRUITMENT_POLICY.SECTIONS.COMPLAINTS.PARAGRAPHS.map(
          paragraph => ({
            text: paragraph,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem',
            whiteSpace: paragraph.includes('\n') ? 'pre-line' : 'normal'
          })
        )}
      />

      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {RECRUITMENT_POLICY.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default RecruitmentPolicyPage;
